'use client';

import { useLayoutEffect, useRef, type RefObject } from 'react';

export const FLAG_ADMIN_SCROLL_ATTR = 'data-flag-admin-scroll';
export const FLAG_ADMIN_STICKY_ATTR = 'data-flag-admin-sticky';

/** The element that actually moves this section. The marked list often grows with its content. */
export function scrollingAncestor(section: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = section.parentElement;
    while (node) {
        const overflowY = getComputedStyle(node).overflowY;
        const canScroll =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            node.scrollHeight > node.clientHeight + 1;
        if (canScroll) return node;
        node = node.parentElement;
    }
    const doc = document.scrollingElement;
    if (doc instanceof HTMLElement && doc.scrollHeight > doc.clientHeight + 1) return doc;
    return null;
}

function stickyCoverHeight(scroller: HTMLElement, section: HTMLElement): number {
    const sticky = scroller.querySelector(`[${FLAG_ADMIN_STICKY_ATTR}]`);
    if (!(sticky instanceof HTMLElement)) return 0;
    const follows = sticky.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING;
    if (!follows) return 0;
    return sticky.getBoundingClientRect().height;
}

/** Put a section at the top of the scroller the user is looking at, under the sticky title. */
export function alignSectionInScroller(section: HTMLElement | null) {
    if (!section) return;
    const scroller = scrollingAncestor(section);
    if (!scroller) return;
    const padTop =
        scroller === document.scrollingElement
            ? 0
            : Number.parseFloat(getComputedStyle(scroller).paddingTop) || 0;
    const delta =
        section.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        padTop -
        stickyCoverHeight(scroller, section);
    if (Math.abs(delta) < 2) return;
    scroller.scrollTop += delta;
}

/** Re-align after a page or search change. The first render is left where it is. */
export function useAlignSectionOnChange(
    sectionRef: RefObject<HTMLElement | null>,
    token: string | number,
) {
    const seen = useRef<string | number | null>(null);

    useLayoutEffect(() => {
        if (seen.current === null || seen.current === token) {
            seen.current = token;
            return;
        }
        seen.current = token;
        const align = () => alignSectionInScroller(sectionRef.current);
        align();
        const frame = requestAnimationFrame(align);
        const soon = window.setTimeout(align, 0);
        const later = window.setTimeout(align, 50);
        return () => {
            cancelAnimationFrame(frame);
            window.clearTimeout(soon);
            window.clearTimeout(later);
        };
    }, [sectionRef, token]);
}
